/**
 * LotteryLimitsPanel — operator-only editor for per-storefront bet caps.
 *
 * Two layers of protection per game:
 *   • max_per_ticket        — single wager on one number can't exceed this
 *   • max_per_number_per_draw — aggregate across all players + cashiers
 *                                on the same (state, number, drawing)
 *
 * Both are enforced server-side in lottery_limits.enforce_ticket_limits().
 * Cashiers see this view in read-only mode so they know the rules.
 *
 * Backed by:
 *   GET /api/operator/lottery/limits  (operator OR cashier)
 *   PUT /api/operator/lottery/limits  (operator only)
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Loader2, RotateCcw, Save, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LotteryLimitsPanel = () => {
    const { user } = useAuth();
    const isOperator = user?.role === 'operator';
    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/operator/lottery/limits`, { withCredentials: true });
            setData(data);
            setDraft(structuredClone(data.limits));
        } catch (err) {
            toast.error('Could not load bet limits');
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            await axios.put(`${API}/operator/lottery/limits`,
                { pick3: draft.pick3, pick4: draft.pick4, big3: draft.big3 },
                { withCredentials: true });
            toast.success('Bet limits updated. Effective immediately.');
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Save failed');
        } finally { setSaving(false); }
    };

    const restoreDefaults = () => {
        if (!data?.defaults) return;
        setDraft(structuredClone(data.defaults));
    };

    if (loading || !draft) {
        return (
            <div className="text-center py-12 text-zinc-500" data-testid="limits-loading">
                <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading bet limits…
            </div>
        );
    }

    const dirty = data && JSON.stringify(draft) !== JSON.stringify(data.limits);

    return (
        <div className="space-y-5" data-testid="lottery-limits-panel">
            <div className="rounded-md border border-emerald-500/30 bg-emerald-950/20 p-4 flex items-start gap-3">
                <ShieldCheck className="w-5 h-5 text-emerald-400 mt-0.5 shrink-0" />
                <div className="text-sm text-zinc-300 leading-relaxed">
                    <p className="font-semibold text-emerald-200">House protection rules</p>
                    <p className="text-xs text-zinc-400 mt-1">
                        Limits apply to <span className="text-zinc-200">every</span> ticket — player self-placed,
                        cashier-placed, and scanned. <span className="text-amber-300">Per-ticket</span> caps a single
                        wager. <span className="text-amber-300">Per-number aggregate</span> caps total exposure across
                        all players on the same number/draw — this is the real bankruptcy guard.
                    </p>
                </div>
            </div>

            {/* Pick 3 */}
            <GameCard title="Pick 3" tone="blue" disabled={!isOperator}>
                <BetTypeRow label="Straight"
                    perTicket={draft.pick3.straight.max_per_ticket}
                    perNumber={draft.pick3.straight.max_per_number_per_draw}
                    onChange={(field, val) => setDraft(d => {
                        const next = structuredClone(d); next.pick3.straight[field] = val; return next;
                    })}
                    disabled={!isOperator} testKey="p3-straight" />
                <BetTypeRow label="Box"
                    perTicket={draft.pick3.box.max_per_ticket}
                    perNumber={draft.pick3.box.max_per_number_per_draw}
                    onChange={(field, val) => setDraft(d => {
                        const next = structuredClone(d); next.pick3.box[field] = val; return next;
                    })}
                    disabled={!isOperator} testKey="p3-box" />
            </GameCard>

            {/* Pick 4 */}
            <GameCard title="Pick 4" tone="purple" disabled={!isOperator}>
                <BetTypeRow label="Straight"
                    perTicket={draft.pick4.straight.max_per_ticket}
                    perNumber={draft.pick4.straight.max_per_number_per_draw}
                    onChange={(field, val) => setDraft(d => {
                        const next = structuredClone(d); next.pick4.straight[field] = val; return next;
                    })}
                    disabled={!isOperator} testKey="p4-straight" />
                <BetTypeRow label="Box"
                    perTicket={draft.pick4.box.max_per_ticket}
                    perNumber={draft.pick4.box.max_per_number_per_draw}
                    onChange={(field, val) => setDraft(d => {
                        const next = structuredClone(d); next.pick4.box[field] = val; return next;
                    })}
                    disabled={!isOperator} testKey="p4-box" />
            </GameCard>

            {/* Big 3 */}
            <GameCard title="Big 3" tone="amber" disabled={!isOperator}
                hint="Big 3 entry is fixed at $10. Only the aggregate per-number cap is enforceable.">
                <BetTypeRow label="(fixed $10 entry)"
                    perTicket={draft.big3.max_per_ticket}
                    perNumber={draft.big3.max_per_number_per_draw}
                    onChange={(field, val) => setDraft(d => {
                        const next = structuredClone(d); next.big3[field] = val; return next;
                    })}
                    disabled={!isOperator} testKey="big3"
                    hideTicketField={true} />
            </GameCard>

            {isOperator && (
                <div className="flex items-center gap-2 sticky bottom-2 z-10">
                    <Button onClick={save} disabled={!dirty || saving}
                        className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-semibold"
                        data-testid="limits-save-btn">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
                        Save limits
                    </Button>
                    <Button variant="outline" onClick={restoreDefaults} disabled={saving}
                        className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"
                        data-testid="limits-restore-defaults">
                        <RotateCcw className="w-4 h-4 mr-1.5" /> Restore defaults
                    </Button>
                    {dirty && <span className="text-xs text-amber-300">Unsaved changes</span>}
                </div>
            )}
            {!isOperator && (
                <p className="text-xs text-zinc-500 italic">
                    Read-only view. Ask the operator to adjust these limits.
                </p>
            )}
        </div>
    );
};

const GameCard = ({ title, tone, children, hint, disabled }) => {
    const tones = {
        blue:   'border-blue-500/30 from-blue-950/30',
        purple: 'border-purple-500/30 from-purple-950/30',
        amber:  'border-amber-500/30 from-amber-950/30',
    };
    return (
        <div className={`rounded-md border bg-gradient-to-br to-zinc-900/30 ${tones[tone] || 'border-zinc-700'}
            ${disabled ? 'opacity-90' : ''}`}>
            <div className="px-5 py-3 border-b border-zinc-800/60 flex items-center justify-between">
                <h4 className="font-semibold text-zinc-100">{title}</h4>
                {hint && <p className="text-[11px] text-zinc-500">{hint}</p>}
            </div>
            <div className="p-5 space-y-3">{children}</div>
        </div>
    );
};

const BetTypeRow = ({ label, perTicket, perNumber, onChange, disabled, testKey, hideTicketField }) => (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr_1fr] gap-3 items-end" data-testid={`limit-row-${testKey}`}>
        <div className="text-sm text-zinc-300">{label}</div>
        {hideTicketField ? <div className="text-xs text-zinc-500 italic">N/A (fixed entry)</div> : (
            <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Max per ticket</Label>
                <div className="relative mt-1">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-300 font-bold pointer-events-none">$</span>
                    <Input type="number" min="0" step="1" value={perTicket}
                        onChange={e => onChange('max_per_ticket', parseFloat(e.target.value) || 0)}
                        disabled={disabled}
                        className="pl-5 bg-zinc-950 border-zinc-700 text-amber-300 font-mono font-bold"
                        data-testid={`limit-${testKey}-per-ticket`} />
                </div>
            </div>
        )}
        <div>
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Max per number / draw</Label>
            <div className="relative mt-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-amber-300 font-bold pointer-events-none">$</span>
                <Input type="number" min="0" step="5" value={perNumber}
                    onChange={e => onChange('max_per_number_per_draw', parseFloat(e.target.value) || 0)}
                    disabled={disabled}
                    className="pl-5 bg-zinc-950 border-zinc-700 text-amber-300 font-mono font-bold"
                    data-testid={`limit-${testKey}-per-number`} />
            </div>
        </div>
    </div>
);

export default LotteryLimitsPanel;
